import { BADGE_FIELD_IDS, TITLE_FIELD_IDS } from "../tableCellFieldValue";
import type { BaseEntity, SimpleTableConfig, SimpleFieldConfig } from "../../../types";

/**
 * Which renderer a cell routes to. Pure decision, no React.
 *
 * Split out of `TableCellRouter.tsx` (module-size lane L17) so the PRECEDENCE
 * — the router's only real logic — is readable in one screen and testable
 * without rendering. Behaviour is pinned by
 * `tests/universal/table-cell-router.test.tsx`.
 *
 * ORDER IS THE CONTRACT, and it is now UNIFORM: EVERY field-ID rule (badge,
 * title, due date, alias columns, QuickBooks realm, `entity_id`) beats a
 * field's declared TYPE, and the type rules (`json`/line items, `tags`) come
 * after. `entity_id` used to sit BELOW the type checks — the one exception —
 * so a tasks `entity_id` column declared `tags` or `json` rendered as tags or
 * raw JSON instead of the CRM link the id rule exists to give it. Reordering
 * these cases changes what renders.
 */
export type CellKind =
  | "badge"
  | "title"
  | "due-date"
  | "email-aliases"
  | "phone-aliases"
  | "realm-badge"
  | "line-items"
  | "json"
  | "tags"
  | "linked-entity"
  | "default";

export function resolveCellKind<TData extends BaseEntity>(
  fieldId: string,
  fieldConfig: SimpleFieldConfig,
  config: SimpleTableConfig<TData>
): CellKind {
  // Badge-style cells (status, priority, entry_type, is_approved, sync_status).
  if (BADGE_FIELD_IDS.includes(fieldId as (typeof BADGE_FIELD_IDS)[number])) return "badge";

  // Title/name fields with view-action support. A field qualifies either by
  // matching the static title-field whitelist OR by being declared as the
  // entity's `titleFieldId` (for entities with no `title`/`name`-style column).
  if (
    TITLE_FIELD_IDS.includes(fieldId as (typeof TITLE_FIELD_IDS)[number]) ||
    fieldId === config.titleFieldId
  ) {
    return "title";
  }

  if (fieldId === "due_date") return "due-date";
  if (fieldId === "email_aliases") return "email-aliases";
  if (fieldId === "phone_aliases") return "phone-aliases";

  // QuickBooks realm as a colored badge (WTC vs Schooley Mitchell).
  // recurring_transactions uses `quickbooks_realm_id`; recurring_charges uses `realm_id`.
  if (fieldId === "quickbooks_realm_id" || fieldId === "realm_id") return "realm-badge";

  // Generic CRM link on tasks: entity_type + entity_id. Last of the field-ID
  // rules, and — like all of them — above the field-TYPE rules below.
  if (fieldId === "entity_id") return "linked-entity";

  if (fieldConfig.type === "json") {
    return fieldConfig.lineItemsType === "staff" || fieldConfig.lineItemsType === "hard-cost"
      ? "line-items"
      : "json";
  }

  if (fieldConfig.type === "tags") return "tags";

  return "default";
}

/**
 * `is_approved` is a boolean rendered as a badge, so it needs words rather than
 * "true"/"false". Every other badge field renders its own value.
 */
export function badgeDisplayText(fieldId: string, fieldValue: unknown): string | undefined {
  if (fieldId !== "is_approved") return undefined;
  return fieldValue ? "Approved" : "Pending";
}
